import { useState } from 'react';
import { Pencil, Plus, X, Check, Save } from 'lucide-react';
import { color, mono } from '../tokens';
import { type CoverageFn } from '../fixtures';
import { useSynth, useSynthProjectId } from '../SynthesisContext';
import { useRoster, type RosterFn } from '../useRoster';
import { asText } from '../asText';

// Coverage answers "did we talk to everyone who matters" — NOT "did we read
// everything" (that is ingestion completeness, a different badge). With no roster
// a function's coverage is UNKNOWN, never 100%: an empty denominator is not a
// full one. (do-first §5.)
//
// The roster (function → target → interviewees) is viz-owned and consultant-editable
// (useRoster). When one exists it drives coverage; otherwise the tab falls back to the
// synthesis payload's own coverage rows, so nothing regresses before a roster is set.
function pct(done: number, need: number): number | null {
  if (!need || need <= 0) return null; // unknown, never 100%
  return Math.round((done / need) * 100);
}

// Roster → the CoverageFn rows the display renders. `done` counts interviewees marked interviewed.
function coverageFromRoster(roster: RosterFn[]): CoverageFn[] {
  return roster.map((r) => {
    const done = r.interviewees.filter((i) => i.interviewed).length;
    const need = Number(r.target) || 0;
    const remaining = need - done;
    return {
      fn: r.fn,
      done,
      need,
      people: r.interviewees.map((i) => i.name),
      gap: remaining > 0 ? `${remaining} more to interview in ${r.fn}` : '',
    };
  });
}

// Seed an editable roster from whatever the synthesis run already found, so the consultant starts
// from the existing coverage rather than a blank slate. People already surfaced are marked interviewed.
function seedFromCoverage(coverage: CoverageFn[]): RosterFn[] {
  return coverage.map((c) => ({
    fn: c.fn,
    target: c.need || c.done || 0,
    // Preserve the original done-count: mark the first `done` people as interviewed, the rest as
    // still-to-do. The payload gives a COUNT, not which specific people, so this is the honest
    // reconstruction the consultant then adjusts — never "everyone is done", which would overstate
    // coverage (the tab's whole point is that unknown is never 100%).
    interviewees: (c.people || []).map((name, i) => ({ name, interviewed: i < (c.done || 0) })),
  }));
}

function CoverageRow({ c }: { c: CoverageFn }) {
  const p = pct(c.done, c.need);
  const complete = p === 100;
  const barColor = p === null ? color.separator : complete ? color.green : color.purple;
  return (
    <div style={{
      background: color.surface, border: `1px solid ${color.hairline}`, borderRadius: 12,
      padding: '14px 18px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: color.ink, flex: 1, minWidth: 0 }}>{asText(c.fn)}</div>
        <span style={{ fontSize: 13, fontFamily: mono, color: color.textMuted }}>
          {c.done}/{c.need || '—'}
        </span>
        <span style={{
          fontSize: 11.5, fontWeight: 700, fontFamily: mono, padding: '2px 8px', borderRadius: 6,
          background: p === null ? color.pillMutedBg : complete ? '#DCFCE7' : color.pillActiveBg,
          color: p === null ? color.textMuted : complete ? '#15803D' : color.purpleDeep,
        }}>
          {p === null ? 'UNKNOWN' : `${p}%`}
        </span>
      </div>

      <div style={{ marginTop: 10, height: 6, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
        <div style={{
          width: p === null ? '0%' : `${p}%`, height: '100%', borderRadius: 999, background: barColor,
        }} />
      </div>

      {c.people?.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {c.people.map((person, i) => (
            <span key={i} style={{
              fontSize: 11.5, padding: '3px 8px', borderRadius: 999,
              background: color.pillMutedBg, color: color.text,
            }}>{asText(person)}</span>
          ))}
        </div>
      )}

      {c.gap && (
        <div style={{
          marginTop: 10, fontSize: 12, color: '#B45309',
          background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 10px',
        }}>
          ⚠ {asText(c.gap)}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  fontSize: 13, padding: '6px 9px', borderRadius: 7, border: `1px solid ${color.hairline}`,
  background: color.surface, color: color.ink, outline: 'none',
};
const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  border: `1px solid ${color.hairline}`, background: color.surface, borderRadius: 7, padding: 5, color: color.textMuted,
};

// The editable roster. Fully controlled — every edit calls onChange with the next roster.
function RosterEditor({ roster, onChange }: { roster: RosterFn[]; onChange: (r: RosterFn[]) => void }) {
  const [bulk, setBulk] = useState('');

  const patchFn = (i: number, patch: Partial<RosterFn>) =>
    onChange(roster.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const removeFn = (i: number) => onChange(roster.filter((_, k) => k !== i));
  const addFn = () => onChange([...roster, { fn: 'New function', target: 1, interviewees: [] }]);

  const patchPerson = (fi: number, pi: number, patch: Partial<RosterFn['interviewees'][number]>) =>
    patchFn(fi, { interviewees: roster[fi].interviewees.map((p, k) => (k === pi ? { ...p, ...patch } : p)) });
  const removePerson = (fi: number, pi: number) =>
    patchFn(fi, { interviewees: roster[fi].interviewees.filter((_, k) => k !== pi) });
  const addPerson = (fi: number) =>
    patchFn(fi, { interviewees: [...roster[fi].interviewees, { name: '', interviewed: false }] });

  // Bulk import: one "Name, Role, Function" per line. Blank Function → "Unassigned".
  const importBulk = () => {
    const byFn = new Map<string, RosterFn>();
    roster.forEach((r) => byFn.set(r.fn, { ...r, interviewees: [...r.interviewees] }));
    bulk.split('\n').map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const [name, role, fn] = line.split(',').map((x) => (x || '').trim());
      if (!name) return;
      const key = fn || 'Unassigned';
      if (!byFn.has(key)) byFn.set(key, { fn: key, target: 0, interviewees: [] });
      byFn.get(key)!.interviewees.push({ name, role: role || undefined, interviewed: false });
    });
    onChange(Array.from(byFn.values()));
    setBulk('');
  };

  return (
    <div>
      {roster.map((r, fi) => (
        <div key={fi} style={{
          border: `1px solid ${color.hairline}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input value={r.fn} onChange={(e) => patchFn(fi, { fn: e.target.value })}
              style={{ ...inputStyle, flex: 1, fontWeight: 600 }} placeholder="Function" />
            <span style={{ fontSize: 11, color: color.textFaint }}>target</span>
            <input type="number" min={0} value={r.target}
              onChange={(e) => patchFn(fi, { target: Math.max(0, Number(e.target.value) || 0) })}
              style={{ ...inputStyle, width: 64 }} />
            <button style={iconBtn} title="Remove function" onClick={() => removeFn(fi)}><X size={14} /></button>
          </div>

          {r.interviewees.map((p, pi) => (
            <div key={pi} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <button
                title={p.interviewed ? 'Interviewed' : 'Not yet interviewed'}
                onClick={() => patchPerson(fi, pi, { interviewed: !p.interviewed })}
                style={{
                  ...iconBtn, width: 26, height: 26,
                  background: p.interviewed ? color.purple : color.surface,
                  color: p.interviewed ? '#fff' : color.textFaint,
                  border: `1px solid ${p.interviewed ? color.purple : color.hairline}`,
                }}
              >{p.interviewed ? <Check size={14} /> : null}</button>
              <input value={p.name} onChange={(e) => patchPerson(fi, pi, { name: e.target.value })}
                style={{ ...inputStyle, flex: 1 }} placeholder="Name" />
              <input value={p.role || ''} onChange={(e) => patchPerson(fi, pi, { role: e.target.value })}
                style={{ ...inputStyle, flex: 1 }} placeholder="Role (optional)" />
              <button style={iconBtn} title="Remove person" onClick={() => removePerson(fi, pi)}><X size={13} /></button>
            </div>
          ))}

          <button onClick={() => addPerson(fi)} style={{
            ...iconBtn, marginTop: 4, gap: 5, padding: '5px 9px', fontSize: 12, color: color.purpleDeep,
          }}><Plus size={13} /> Add interviewee</button>
        </div>
      ))}

      <button onClick={addFn} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
        padding: '8px 12px', borderRadius: 8, background: color.surfaceAlt, color: color.text,
        border: `1px dashed ${color.separator}`, marginBottom: 16,
      }}><Plus size={14} /> Add function</button>

      {/* Bulk import (Ryan: "upload a list of people we need to talk to"). */}
      <div style={{ border: `1px solid ${color.hairline}`, borderRadius: 12, padding: '12px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: color.text, marginBottom: 6 }}>
          Paste a roster — one <span style={{ fontFamily: mono }}>Name, Role, Function</span> per line
        </div>
        <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={4}
          placeholder={'Tamara Denton, Lead Data Engineer, Data Engineering\nMia Cho, BI Manager, BI'}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: mono, fontSize: 12 }} />
        <button onClick={importBulk} disabled={!bulk.trim()} style={{
          marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
          padding: '7px 12px', borderRadius: 8, border: 'none', cursor: bulk.trim() ? 'pointer' : 'not-allowed',
          background: bulk.trim() ? color.purple : color.hairline, color: bulk.trim() ? '#fff' : color.textFaint,
        }}><Plus size={14} /> Import into roster</button>
      </div>
    </div>
  );
}

export default function Coverage() {
  const projectId = useSynthProjectId();
  const { coverage } = useSynth();
  const { roster, saving, editable, save } = useRoster(projectId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RosterFn[]>([]);

  const hasRoster = roster.length > 0;
  // The rows the display renders: the roster when one exists, else the synthesis payload's coverage.
  const rows: CoverageFn[] = hasRoster ? coverageFromRoster(roster) : coverage;

  const totalDone = rows.reduce((a, c) => a + c.done, 0);
  const totalNeed = rows.reduce((a, c) => a + (c.need || 0), 0);
  const overall = pct(totalDone, totalNeed);
  const gaps = rows.filter((c) => c.gap).length;

  const startEdit = () => {
    setDraft(hasRoster ? roster : seedFromCoverage(coverage));
    setEditing(true);
  };
  const saveEdit = async () => { await save(draft); setEditing(false); };

  return (
    <div>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>INTERVIEW COVERAGE</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Who we talked to — by function
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Coverage is against the engagement roster, not the documents read. A function with no roster reads{' '}
            <strong style={{ color: color.text }}>unknown</strong> — never 100%.
          </p>
        </div>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setEditing(false)} style={{
              fontSize: 13, cursor: 'pointer', padding: '8px 12px', borderRadius: 8,
              background: color.surface, color: color.text, border: `1px solid ${color.hairline}`,
            }}>Cancel</button>
            <button onClick={saveEdit} disabled={saving} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
              cursor: saving ? 'wait' : 'pointer', padding: '8px 12px', borderRadius: 8, border: 'none',
              background: color.ink, color: '#fff',
            }}><Save size={14} /> {saving ? 'Saving…' : editable ? 'Save roster' : 'Apply (local)'}</button>
          </div>
        ) : (
          <button onClick={startEdit} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
            padding: '8px 12px', borderRadius: 8, background: color.surface, color: color.text,
            border: `1px solid ${color.hairline}`,
          }}><Pencil size={14} /> {hasRoster ? 'Edit roster' : 'Set roster'}</button>
        )}
      </div>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {editing ? (
        <>
          {!editable && (
            <div style={{
              marginBottom: 14, fontSize: 12, color: '#B45309',
              background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 12px',
            }}>
              No project is open — edits apply to this view only and won’t be saved.
            </div>
          )}
          <RosterEditor roster={draft} onChange={setDraft} />
        </>
      ) : (
        <>
          {rows.length === 0 ? (
            <div style={{
              marginBottom: 16, fontSize: 12.5, color: '#92400E',
              background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 12px',
              lineHeight: 1.5,
            }}>
              Coverage: unknown — no interview roster was supplied. "We read every file" (ingestion) and "we talked to everyone who matters" (coverage) are different claims — with no roster, coverage renders unknown rather than 100%.
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
                border: `1px solid ${color.hairline}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20,
              }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>OVERALL</div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, fontFamily: mono, color: color.ink }}>
                    {overall === null ? 'UNKNOWN' : `${overall}%`}
                  </div>
                </div>
                <div style={{ borderLeft: `1px solid ${color.hairline}`, paddingLeft: 24 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>INTERVIEWED</div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, fontFamily: mono, color: color.ink }}>
                    {totalDone}<span style={{ fontSize: 13, color: color.textFaint, fontWeight: 400 }}>/{totalNeed || '—'}</span>
                  </div>
                </div>
                <div style={{ borderLeft: `1px solid ${color.hairline}`, paddingLeft: 24 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>OPEN GAPS</div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, fontFamily: mono, color: gaps ? '#B45309' : color.ink }}>
                    {gaps}
                  </div>
                </div>
              </div>
              {rows.map((c) => <CoverageRow key={c.fn} c={c} />)}
            </>
          )}
        </>
      )}
    </div>
  );
}
